import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled, { DefaultTheme } from 'styled-components';

import {
  SkillMatrixSegment,
  SkillMatrixSegmentValue,
} from 'src/common/components/skill-matrix-graphic/skill-matrix-graphic.types';

const getSegmentColor = (theme: DefaultTheme, segment: SkillMatrixSegmentValue): string => {
  switch (segment) {
    case SkillMatrixSegment.ENTRY: {
      return theme.colors.success.main;
    }
    case SkillMatrixSegment.JUNIOR: {
      return theme.colors.warning.light;
    }
    case SkillMatrixSegment.MID: {
      return theme.colors.error.main;
    }
    case SkillMatrixSegment.SENIOR: {
      return theme.colors.level.main;
    }
    default: {
      return theme.colors.text.main;
    }
  }
};

export const StyledContentWrapper = styled.div`
  background: ${({ theme }) => theme.colors.background.lighter};
  border-radius: 0 0.5rem 0.5rem 0;
  height: 100%;
  padding: 3.2rem;
`;

export const StyledHeader = styled.header`
  margin-bottom: 3.2rem;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1.6rem;
  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    grid-template-columns: 1fr;
  }
`;

export const StyledTitleWrapper = styled.div`
  span {
    display: flex;
    align-items: center;
  }

  h2 {
    font-size: 2.4rem;
    margin-bottom: 0.8rem;
    @media (max-width: 1070px) and (min-width: ${({ theme }) => theme.sizes.md}) {
      font-size: 2.2rem;
    }

    @media (max-width: ${({ theme }) => theme.sizes.md}) {
      font-size: 2rem;
    }
  }

  p {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  svg {
    margin-right: 1rem;
    height: 2.1rem;
    path {
      fill: ${({ theme }) => theme.colors.grey[500]};
    }
  }

  &[data-selected='true'] {
    svg path {
      fill: ${({ theme }) => theme.colors.success.main};
    }
  }

  &[data-skill='foundational'] {
    svg path {
      fill: ${({ theme }) => theme.colors.success.main};
    }
  }

  &[data-skill='analyst'] {
    svg path,
    .fa-primary,
    .fa-secondary {
      fill: ${({ theme }) => theme.colors.info.main};
    }
  }

  &[data-skill='penetration'] {
    svg path,
    .fa-primary,
    .fa-secondary {
      fill: ${({ theme }) => theme.colors.error.main};
    }
  }

  &[data-skill='engineer'] {
    svg path,
    .fa-primary,
    .fa-secondary {
      fill: ${({ theme }) => theme.colors.warning.main};
    }
  }
`;

export const StyledSegmentWrapper = styled.div<{ $isDarkMode: boolean; $segment: SkillMatrixSegmentValue }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  button {
    min-width: 16rem;
    svg {
      .fa-primary,
      .fa-secondary {
        fill: ${({ theme, $segment }) => getSegmentColor(theme, $segment)};
      }
    }
  }
  .segment-select > div > div {
    background-color: ${({ theme, $isDarkMode }) => $isDarkMode && theme.colors.secondary.main};
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.6rem;
  }
`;

export const StyledSelectIcon = styled(FontAwesomeIcon)<{
  $segment: SkillMatrixSegmentValue;
  $isSelected?: boolean;
  $isDarkMode?: boolean;
}>`
  path,
  .fa-primary,
  .fa-secondary {
    fill: ${({ theme, $segment, $isSelected, $isDarkMode }) => {
      if ($isSelected) {
        return $isDarkMode ? theme.colors.secondary.main : theme.colors.white;
      }
      return getSegmentColor(theme, $segment);
    }};
  }

  &:hover,
  &:focus {
    path,
    .fa-primary,
    .fa-secondary {
      fill: ${({ theme }) => theme.colors.success.main};
    }
  }
`;
